// @ds/connected/ConfirmProvider — imperative confirm backed by the DS Modal.
//
// Renders a single DS `<Modal>` and exposes `useConfirm()` → a function that
// returns `Promise<boolean>`. Lets a handler `await ctx.confirm(...)` inline
// (e.g. a destructive row action) without any layout/shell hosting modal JSX.
// Mounted once near the app root.

import React, {
  useCallback,
  useState,
  type ReactNode,
} from "react";
import {
  ConfirmContext,
  type ConfirmFn,
  type ConfirmOptions,
} from "@ds/connected/confirmContext";

export type { ConfirmOptions, ConfirmFn } from "@ds/connected/confirmContext";
import { useTranslation } from "react-i18next";
import { Modal } from "@ds/primitives/Modal";
import { Icon } from "@ds/display/Icon";

interface PendingConfirm {
  opts: ConfirmOptions;
  resolve: (value: boolean) => void;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>(
    (opts) => new Promise<boolean>((resolve) => setPending({ opts, resolve })),
    [],
  );

  const settle = useCallback(
    (value: boolean) => {
      pending?.resolve(value);
      setPending(null);
    },
    [pending],
  );

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && (
        <Modal
          open
          icon={<Icon name={pending.opts.danger ? "delete" : "warning"} size="md" tone="current" />}
          iconTone={pending.opts.danger ? "red" : "blue"}
          title={t(pending.opts.titleKey, pending.opts.titleKey)}
          body={
            pending.opts.body ??
            t("common.confirm_delete_description", "Tej operacji nie można cofnąć.")
          }
          width={420}
          testId="ds-confirm"
          onClose={() => settle(false)}
          actions={[
            {
              id: "cancel",
              label: t("common.actions.cancel", "Anuluj"),
              variant: "default",
              onTrigger: () => settle(false),
            },
            {
              id: "confirm",
              label: pending.opts.okKey
                ? t(pending.opts.okKey, pending.opts.okKey)
                : t("common.actions.confirm", "Potwierdź"),
              variant: pending.opts.danger ? "danger" : "primary",
              onTrigger: () => settle(true),
            },
          ]}
        />
      )}
    </ConfirmContext.Provider>
  );
}
